import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SEO, Hero } from '../components/Shared';
import { quizBank } from '../quizBank';
import { useAuth } from '../components/AuthContext';
import { storageService } from '../services/storage';
import { getArcticCoachUrl } from '../constants/config';

const QuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const quiz = quizBank[id || ''];
  const { user, anonymousId } = useAuth();
  const navigate = useNavigate();
  
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [previousAttempts, setPreviousAttempts] = useState<any[]>([]);

  useEffect(() => {
    const fetchAttempts = async () => {
      const userId = user ? user.id : anonymousId;
      const attempts = await storageService.getAttempts(userId);
      setPreviousAttempts(attempts.filter((a: any) => a.quizId === quiz?.quizId));
    };
    if (quiz) fetchAttempts();
  }, [quiz, user, anonymousId]);

  if (!quiz) return <div className="p-20 text-center text-slate-400 font-bold">Tenttiä ei löydy.</div>;

  const handleAnswer = (qId: string, val: string | boolean) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qId]: val }));
  };

  const calculateScore = () => {
    let score = 0;
    quiz.questions.forEach(q => {
      if (answers[q.id] === q.answer) score++;
    });
    return score;
  };

  const score = calculateScore();
  const passed = score >= quiz.questions.length * 0.7;

  const handleSubmit = async () => {
    setSubmitted(true);
    const userId = user ? user.id : anonymousId;
    await storageService.saveQuizAttempt({
      id: Math.random().toString(36).substring(7),
      userId,
      quizId: quiz.quizId,
      score,
      total: quiz.questions.length,
      passed,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-20">
      <SEO title={`${quiz.title} | Lapland AI Lab`} description="Testaa tietoturvaosaamisesi ja ansaitse sertifikaatti." />
      <Hero title={quiz.title} subtitle="Vastaa kysymyksiin huolellisesti. 70% oikein vaaditaan läpäisyyn." label="Arctic Certification" />

      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Istunto:</span>
          <span className="text-xs text-slate-500 font-mono">{user ? user.email : 'Anonyymi (' + anonymousId + ')'}</span>
        </div>
        
        <div className="flex items-center gap-6">
          <a 
            href={getArcticCoachUrl('quiz')} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] font-black text-amber-400 hover:text-amber-300 uppercase tracking-widest flex items-center gap-2 transition-colors bg-amber-500/5 px-3 py-1.5 rounded-lg border border-amber-500/10"
          >
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            Harjoittele GPT:n kanssa
          </a>
          
          {previousAttempts.length > 0 && (
            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              Aiemmat yritykset: {previousAttempts.length}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8 mb-12">
        {quiz.questions.map((q, idx) => (
          <div key={q.id} className={`glass p-10 rounded-[2.5rem] border transition-all ${submitted ? (answers[q.id] === q.answer ? 'border-emerald-500/30' : 'border-red-500/30') : 'border-slate-800'}`}>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4 block">Kysymys {idx + 1} / {quiz.questions.length}</span>
            <h3 className="text-xl font-bold text-white mb-8 leading-tight">{q.prompt}</h3>
            
            <div className="grid gap-4">
              {q.type === 'multiple-choice' ? (
                q.options?.map(opt => (
                  <button
                    key={opt}
                    onClick={() => handleAnswer(q.id, opt)}
                    className={`p-5 rounded-2xl border text-left transition-all ${answers[q.id] === opt ? 'bg-emerald-500 text-slate-950 border-emerald-500 font-bold' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-600'}`}
                  >
                    {opt}
                  </button>
                ))
              ) : (
                <div className="flex gap-4">
                  {[true, false].map(val => (
                    <button
                      key={val.toString()}
                      onClick={() => handleAnswer(q.id, val)}
                      className={`flex-1 p-5 rounded-2xl border text-center transition-all ${answers[q.id] === val ? 'bg-emerald-500 text-slate-950 border-emerald-500 font-bold' : 'bg-slate-900 border-slate-800 text-slate-400'}`}
                    >
                      {val ? 'Totta' : 'Tarua'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {submitted && (
              <div className={`mt-8 p-6 rounded-2xl bg-slate-950/50 border ${answers[q.id] === q.answer ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                <p className={`text-sm font-bold mb-2 ${answers[q.id] === q.answer ? 'text-emerald-400' : 'text-red-400'}`}>
                  {answers[q.id] === q.answer ? '✓ Oikein!' : `✕ Väärin. Oikea vastaus: ${q.answer === true ? 'Totta' : q.answer === false ? 'Tarua' : q.answer}`}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">{q.explanation}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {!submitted ? (
        <button 
          onClick={handleSubmit}
          disabled={Object.keys(answers).length < quiz.questions.length}
          className="w-full bg-emerald-500 text-slate-950 font-black py-6 rounded-3xl uppercase tracking-widest shadow-xl shadow-emerald-500/20 disabled:opacity-50 active:scale-95 transition-transform"
        >
          Palauta vastaukset
        </button>
      ) : (
        <div className="text-center glass p-10 rounded-[3rem] border-emerald-500/20 animate-in zoom-in duration-500">
          <h2 className="text-5xl font-black text-white mb-4">{score} / {quiz.questions.length}</h2>
          <p className={`text-xl font-bold mb-8 ${passed ? 'text-emerald-400' : 'text-red-400'}`}>
            {passed ? 'Läpäisty! Olet Arctic Security -asiantuntija.' : 'Ei läpäisty. Kertaa materiaali ja yritä uudelleen.'}
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            {passed && quiz.quizId === 'q-master' && (
              <Link 
                to={`/certificate?name=${user?.email.split('@')[0] || 'Arctic Pro'}`} 
                className="bg-indigo-500 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-indigo-500/20 hover:scale-105 transition-transform"
              >
                Lataa Sertifikaatti
              </Link>
            )}
            <Link to="/" className="inline-block bg-slate-800 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-700 transition-colors">
              Palaa etusivulle
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizPage;
